import React, { useEffect, useState } from "react";
import { connect, ConnectedProps } from "react-redux";

import { buyItem, cancelItem, finishQuest } from "../../../../store/actions";
import { UPGRADES } from "../../../../lib/enums";
import { Quest, Upgrade } from "../../../../lib/types";
import SubMenu from "../../../Menu/SubMenu";
import DialogButton from "../../../Common/DialogButton";

import { icons } from "../../../../assets/images/icons";
import EmptySlot from "./EmptySlot";
import { setSameHeight } from "../../../../lib/elementUtilities";
import Countdown from "../../../Common/Countdown";

interface IProps {
  type?: string;
}

const mapStateToProps = (state: any) => ({
  resources: state.governorate.resources,
  marketItems: state.governorate.marketItems,
  marketLimit: state.governorate.marketLimit,
  marketUsage: state.governorate.marketUsage,
  extraMarketSlots: state.governorate.extraMarketSlots,
  credits: state.governorate.player.credits,
  instantMarket: state.governorate.upgrades.find((u: Upgrade) => u.id === UPGRADES.InstantMarket),
  newMarketSlot: state.governorate.upgrades.find((u: Upgrade) => u.id === UPGRADES.MarketSlot),
  availableQuests: state.governorate.availableQuests,
  finishedQuests: state.governorate.finishedQuests
})

const mapDispatchToProps = (dispatch: any) => ({
  cancelItemDispatch: (itemId: number) => dispatch(cancelItem(itemId)),
  buyItemDispatch: (itemId: number | string) => dispatch(buyItem(itemId)),
  finishQuestDispatch: (questId: number) => dispatch(finishQuest(questId))
});

const connector = connect(mapStateToProps, mapDispatchToProps);

const Marketplace = ({
  resources,
  marketItems,
  marketLimit,
  marketUsage,
  extraMarketSlots,
  credits,
  instantMarket,
  newMarketSlot,
  finishedQuests,
  availableQuests,
  cancelItemDispatch,
  buyItemDispatch,
  finishQuestDispatch,
  type = "world"
}: ConnectedProps<typeof connector> & IProps) => {
  useEffect(() => {
    if (availableQuests?.some((q: Quest) => q.id === 19)
      && !finishedQuests?.some((q: Quest) => q.id === 19)) {
      finishQuestDispatch(19);
    }
  }, [])

  useEffect(() => {
    setSameHeight();
  }, [marketItems, extraMarketSlots]);

  const [marketLimitReached, setMarketLimitReached] = useState<boolean>(marketUsage >= marketLimit);

  useEffect(() => {
    setMarketLimitReached(marketUsage >= marketLimit);
  }, [marketUsage])

  return (
    <>
      <SubMenu
        tabs={[
          {
            name: "AllianceStorage",
            title: "Storage",
            icon: "trziste",
            label: "Alliance Storage",
            active: false
          },
          {
            name: "Marketplace",
            title: "Marketplace",
            icon: "trziste",
            label: "World Market",
            active: true,
            parameters: {
              type: "world",
            }
          }
        ]}
      />

      <>
        <section className="boxes-group">
          <h2 className="h2 h2-first">
            <span className="bordered">Sell</span>
          </h2>
          <span className="divider"/>
          <div className="box">
            <p className={`text-bigger ${marketLimitReached ? "negative" : ""}`}>Market limit {marketUsage}/{marketLimit}</p>
            <div className="row persons market-items" id="sell-slots">
              {Array(5 + extraMarketSlots).fill("").map((_, index: number) => {
                const item = marketItems.governorate[index];

                return (<div className="col-sm-4 col-md-2">
                  <div className={`person match-height no-min-height ${!item && marketLimitReached ? "disabled" : ""}`}>
                    {item
                      ? <>
                        <span>{item.isAlliance ? "Alliance" : "World"}</span><br/>
                        <span className="white">
                      {item.amount}
                    </span> <i className={`icon icon-standalone icon-sm icon-${item.resourceIcon} middle`}/><br/>
                        <span className="white">
                      {marketItems.governorate[index].price}
                    </span> <i className="icon icon-standalone icon-sm icon-trziste gold middle"/><br/>
                        {!item.available
                          ? <>
                            <h3 className="h3">Time to market</h3>
                            <p className="">
                              <span className="negative"><Countdown toTime={new Date(item.placed * 1000 + 600000)} /></span>
                            </p>
                            <DialogButton
                              dialogOptions={{
                                name: "PurchaseUpgrade",
                                header: "Instant market",
                                id: UPGRADES.InstantMarket,
                                params: { itemId: item.id },
                                question: "Skip market wait time?",
                                purchasable: !(credits < instantMarket.price),
                                price: instantMarket.price
                              }}
                            >
                              GET <img src={icons.credit} alt="" />
                            </DialogButton>
                          </>
                          : <a
                            className="btn btn-sm btn-blank"
                            onClick={() => cancelItemDispatch(item.id)}
                          >
                            Cancel
                          </a>}
                      </>
                      : <EmptySlot type={type} disabled={marketLimitReached}/>}
                  </div>
                </div>)
              })}
              <div className="col-sm-4 col-md-2">
                <div className="person no-min-height match-height" style={{ height: "107px" }}>
                  <p>New Market slot</p>
                  <DialogButton
                    dialogOptions={{
                      name: "PurchaseUpgrade",
                      header: "New market slot",
                      id: UPGRADES.MarketSlot,
                      question: "Purchase new market slot?",
                      purchasable: !(credits < newMarketSlot.price),
                      price: newMarketSlot.price
                    }}
                  >
                    GET <img src={icons.credit} alt="" />
                  </DialogButton>
                </div>
              </div>
            </div>
          </div>
          <span className="divider"/>
        </section>

        <section className="boxes-group">
          <h2 className="h2">
            <span className="bordered">Buy</span>
          </h2>
          <span className="divider"/>
          <div className="box">
            <div className="row persons market-items">
              {marketItems.reserves.filter((item: any) => item.resource.name !== "gold")
                .map((item: any) => <div className="col-sm-4 col-md-2">
                  <div
                    className={`person ${marketLimit - marketUsage < 100
                    || !resources.ducats || !item || resources.ducats < item.price
                      ? "disabled"
                      : ""} no-min-height`}
                  >
              <span className="white">
                {item.amount}
              </span> <i className={`icon icon-standalone icon-sm icon-${item.resource.icon} middle`}/><br/>
                    <span className="white">
                {item.price}
              </span> <i className="icon icon-standalone icon-sm icon-trziste gold middle"/><br/>
                    <a
                      className="btn btn-sm btn-blank"
                      onClick={() => !(marketLimit - marketUsage < 100
                        || !resources.ducats || !item || resources.ducats < item.price)
                        && buyItemDispatch(item.id)}
                    >
                      Buy
                    </a>
                  </div>
                </div>)}
            </div>
          </div>
        </section>
      </>
    </>
  )
};

export default connector(Marketplace);
